import { Modal, Radio, Segmented, Tooltip } from 'antd';
import OpterraPhoenixLogo from 'assets/images/OpterraPhoenixLogo.png';
import Notification from 'components/common/Notification';
import {
  CASHFLOW_DETAIL_CATEGORIES,
  CASHFLOW_DETAIL_PRODUCTS,
  CASHFLOW_PERIOD_MONTH,
  CASHFLOW_PERIOD_QUARTER,
  CASHFLOW_YEAR_CALENDAR,
  CASHFLOW_YEAR_FINANCIAL,
  SUMMARY_SCOPE_HINT_STORAGE_KEY,
  TREE_YEAR_ALL,
  FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL,
  FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL,
} from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuInfo } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import SectionCard from 'pages/FarmsPage/Workspace/Insights/summary2/SectionCard';
import { formatCurrency } from 'utils/CurrencyUtils';
import {
  type CashflowDetailLevel,
  type CashflowPeriod,
  type CashflowYearMode,
  setCashflowCategory,
  setCashflowDetailLevel,
  setCashflowPeriod,
  setCashflowYearMode,
  setSummaryHintVisible,
} from '../../../../../redux/slices/InsightsSlice';
import CopyButton from '../summary2/CopyButton';
import { formatCashflowYearLabel } from './cashflowUtils';
import CashflowGrid from './CashflowGrid';
import { computeCashflow, type CashflowLine } from './computeCashflow';

interface CashflowViewProps {
  inputs: IInput[];
  gUnits: GUnit[];
  /** Name of whichever tree level is in scope — paddock, farm, or org. */
  scopeName?: string;
}

/** A labelled cluster of related ribbon controls, mirroring AnalysisView's local RibbonGroup (not exported there — each lens owns its own copy of this tiny layout primitive). */
const RibbonGroup: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-bold uppercase tracking-wide text-gray-muted">
      {label}
    </span>
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

/** One control row inside a RibbonGroup — mirrors AnalysisView's local RibbonRow. */
const RibbonRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center gap-2 rounded-md border border-gray-smoke bg-white px-3 py-1.5">
    <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-gray-muted">
      {label}:
    </span>
    {children}
  </div>
);

const RibbonDivider: React.FC = () => <span className="w-px bg-gray-argent" />;

const CashflowView: React.FC<CashflowViewProps> = ({
  inputs,
  gUnits,
  scopeName,
}) => {
  const dispatch = useDispatch();
  const {
    cashflowCategory,
    cashflowPeriod,
    cashflowYearMode,
    cashflowDetailLevel,
    summaryHintVisible,
  } = useSelector((state: RootState) => state.insightsReducer);
  // The year itself is governed by the Farm/Paddock tree's own year filter,
  // not a Cashflow-specific control — when the tree is set to "All years"
  // there's no single year to build period columns around, so this falls
  // back to the current year (same fallback TreeViewControls.tsx uses when
  // stepping out of "All years").
  const treeYearFilter = useSelector(
    (state: RootState) => state.farmReducer.treeYearFilter
  );
  const isAllYears = treeYearFilter === TREE_YEAR_ALL;
  const effectiveYear = isAllYears ? new Date().getFullYear() : treeYearFilter;

  const [isRibbonExpanded, setIsRibbonExpanded] = useState(true);
  // A hover tooltip isn't enough room to explain this unambiguously. A
  // typical crop season straddles the financial year boundary (planting in
  // one calendar year, harvest in the next), so a farmer needs to know that
  // each line is dated by its own task/step, not by the season or the
  // plan's nominal year. A modal gives room to explain that clearly.
  const [isFyInfoOpen, setIsFyInfoOpen] = useState(false);

  // CashflowGrid owns the range-selection state itself, but reports it up
  // here so the Copy to clipboard button can act on the selection instead
  // of the whole grid while one is active — otherwise this button would
  // silently copy everything even when a user has just selected a few
  // cells and would reasonably expect it to act on that selection.
  // hasSelection is real state (drives the button's label); the getter
  // itself is a ref since only *whether* there's a selection needs to
  // trigger a re-render, not every keystroke of a drag.
  const [hasSelection, setHasSelection] = useState(false);
  const getSelectionTsvRef = useRef<() => string>(() => '');
  const handleSelectionChange = useCallback(
    (selected: boolean, getSelectionTsv: () => string) => {
      setHasSelection(selected);
      getSelectionTsvRef.current = getSelectionTsv;
    },
    []
  );

  const showProducts = cashflowDetailLevel === CASHFLOW_DETAIL_PRODUCTS;

  const ribbonSummary = [
    cashflowCategory === InputTaskCategory.Plan ? 'Plan' : 'Actual',
    showProducts ? 'Products' : 'Categories',
    cashflowPeriod === CASHFLOW_PERIOD_QUARTER ? 'Quarter' : 'Month',
    cashflowYearMode === CASHFLOW_YEAR_FINANCIAL ? 'Financial year' : 'Calendar year',
  ].join(' · ');

  const handleCloseScopeHint = useCallback(() => {
    localStorage.setItem(SUMMARY_SCOPE_HINT_STORAGE_KEY, 'false');
    dispatch(setSummaryHintVisible(false));
  }, [dispatch]);

  const data = useMemo(
    () =>
      computeCashflow(
        inputs,
        cashflowCategory,
        cashflowPeriod,
        effectiveYear,
        cashflowYearMode,
        gUnits
      ),
    [inputs, cashflowCategory, cashflowPeriod, effectiveYear, cashflowYearMode, gUnits]
  );

  const buildWholeGridTsv = useCallback(() => {
    const lineCols = (line: CashflowLine, blankTotal = false): string[] => [
      line.label,
      blankTotal ? '' : formatCurrency(line.values[0]),
      ...line.values.slice(1).map(formatCurrency),
    ];

    const rows: string[] = [];
    const yearLabel = formatCashflowYearLabel(effectiveYear, cashflowYearMode);
    rows.push([scopeName, yearLabel].filter(Boolean).join(' - '));
    rows.push(
      ['Line item', 'Total', 'No Date', ...data.periods.map((p) => p.label)].join(
        '\t'
      )
    );

    rows.push('Income');
    if (showProducts) {
      data.incomeLines.forEach((line) => rows.push(lineCols(line).join('\t')));
    }
    rows.push(lineCols(data.totalIncome).join('\t'));

    rows.push('Expenditure');
    if (showProducts) {
      data.expenditureCategories.forEach((group) => {
        group.products.forEach((product) =>
          rows.push(lineCols(product).join('\t'))
        );
      });
    } else {
      data.expenditureCategories.forEach((group) => {
        rows.push(lineCols(group.total).join('\t'));
      });
    }
    rows.push(lineCols(data.totalExpenditure).join('\t'));

    rows.push(lineCols(data.balance).join('\t'));
    // Cash Flow's Total column isn't meaningful (a running balance can't
    // be summed), so it's left blank in the export, matching the grid's "—".
    rows.push(lineCols(data.cashFlow, true).join('\t'));

    return rows.join('\n');
  }, [data, scopeName, effectiveYear, cashflowYearMode, showProducts]);

  // Copies the active selection instead of the whole grid whenever one
  // exists — see handleSelectionChange's comment above.
  const { copied, copy } = useCopyToClipboard(
    useCallback(
      () => (hasSelection ? getSelectionTsvRef.current() : buildWholeGridTsv()),
      [hasSelection, buildWholeGridTsv]
    )
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden">
      {summaryHintVisible && (
        <Notification
          type="primary"
          className="w-fit"
          closable
          handleClose={handleCloseScopeHint}
          icon={
            <img
              src={OpterraPhoenixLogo}
              alt="Logo"
              className="mt-1 h-4 w-auto brightness-0 invert"
            />
          }
        >
          <span className="text-sm">
            Use <b>Farm / Paddock Tree</b> on the left to set cashflow scope.
          </span>
        </Notification>
      )}

      {/* Same "config" card as Farm diagnostics' Yearly Overview, sized to
          its controls rather than stretching across the grid below. */}
      <div className="w-fit max-w-full">
        <SectionCard
          id="cashflow-settings"
          title="Cashflow settings"
          variant="config"
          collapsed={!isRibbonExpanded}
          actions={
            <button
              type="button"
              className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
              onClick={() => setIsRibbonExpanded((v) => !v)}
            >
              {isRibbonExpanded ? (
                <LuChevronUp size={14} />
              ) : (
                <LuChevronDown size={14} />
              )}
              {isRibbonExpanded
                ? FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL
                : FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL}
            </button>
          }
        >
          {isRibbonExpanded ? (
              <div className="flex flex-row flex-wrap items-start gap-6">
                <RibbonGroup label="View options">
                  <RibbonRow label="Status">
                    <Radio.Group
                      value={cashflowCategory}
                      onChange={(e) =>
                        dispatch(
                          setCashflowCategory(e.target.value as InputTaskCategory)
                        )
                      }
                    >
                      <Radio value={InputTaskCategory.Plan}>Plan</Radio>
                      <Radio value={InputTaskCategory.Actual}>Actual</Radio>
                    </Radio.Group>
                  </RibbonRow>
                  <RibbonRow label="Detail">
                    <Segmented<CashflowDetailLevel>
                      value={cashflowDetailLevel}
                      onChange={(value) => dispatch(setCashflowDetailLevel(value))}
                      options={[
                        { label: 'Categories', value: CASHFLOW_DETAIL_CATEGORIES },
                        { label: 'Products', value: CASHFLOW_DETAIL_PRODUCTS },
                      ]}
                    />
                  </RibbonRow>
                </RibbonGroup>

                <RibbonDivider />

                <RibbonGroup label="Period">
                  <RibbonRow label="Show">
                    <Segmented<CashflowPeriod>
                      value={cashflowPeriod}
                      onChange={(value) => dispatch(setCashflowPeriod(value))}
                      options={[
                        { label: 'Month', value: CASHFLOW_PERIOD_MONTH },
                        { label: 'Quarter', value: CASHFLOW_PERIOD_QUARTER },
                      ]}
                    />
                  </RibbonRow>
                  <RibbonRow label="Year type">
                    <Segmented<CashflowYearMode>
                      value={cashflowYearMode}
                      onChange={(value) => dispatch(setCashflowYearMode(value))}
                      options={[
                        { label: 'Calendar', value: CASHFLOW_YEAR_CALENDAR },
                        { label: 'Financial', value: CASHFLOW_YEAR_FINANCIAL },
                      ]}
                    />
                    <Tooltip title="How costs and income are attributed to periods">
                      <button
                        type="button"
                        aria-label="How costs and income are attributed to periods"
                        className="inline-flex cursor-pointer items-center justify-center rounded-full border border-transparent bg-transparent p-1 text-gray-dark hover:border-charcoal hover:bg-rowHover hover:text-charcoal"
                        onClick={() => setIsFyInfoOpen(true)}
                      >
                        <LuInfo size={14} />
                      </button>
                    </Tooltip>
                  </RibbonRow>
                </RibbonGroup>
              </div>
          ) : (
            <span className="text-sm text-gray-dark">{ribbonSummary}</span>
          )}
        </SectionCard>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm font-bold text-charcoal">
        <span className="flex items-center gap-2">
          {scopeName && <span>{scopeName}</span>}
          <span className="font-normal text-gray-muted">
            {formatCashflowYearLabel(effectiveYear, cashflowYearMode)}
          </span>
          {isAllYears && (
            <Tooltip title="The Farm/Paddock tree is set to All years. Pick a specific year there to change which year Cashflow shows.">
              <span className="cursor-help text-xs font-normal text-gray-muted">
                (default)
              </span>
            </Tooltip>
          )}
        </span>
        <CopyButton
          copied={copied}
          onCopy={copy}
          label={hasSelection ? 'Copy selection' : 'Copy to clipboard'}
        />
      </div>

      <CashflowGrid
        data={data}
        detailLevel={cashflowDetailLevel}
        onSelectionChange={handleSelectionChange}
      />

      <Modal
        title="How costs and income are attributed to periods"
        open={isFyInfoOpen}
        onCancel={() => setIsFyInfoOpen(false)}
        footer={null}
        // Pins the zoom-in transform-origin to a fixed point instead of
        // antd's default of zooming out from wherever the trigger icon was
        // clicked — same override DeleteOrganisationModal.tsx uses for its
        // pop-in animation.
        mousePosition={{ x: 0, y: 0 }}
      >
        <div className="flex flex-col gap-3 text-sm text-gray-dark">
          <p>
            Every cost or income line is placed by the date of the task it
            belongs to. It is not placed by the plan&apos;s overall season,
            or by the year the plan was created.
          </p>
          <p>
            &ldquo;Calendar&rdquo; groups dates into January through December
            of the selected year. &ldquo;Financial&rdquo; groups dates into
            July through June.
          </p>
          <p>
            Switching between Calendar and Financial changes the 12-month
            window itself, so a line can appear in one view and fall outside
            the other.
          </p>
          <p>
            The year is set using the control at the top of the Farm/Paddock
            tree view on the left side of the screen.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CashflowView;
